import {DictionaryItem, PageSchema, ParamsType} from 'utils';
import api, {instance} from 'utils/api';
import {
    AdditionalWorkByWellResponse,
    CreateFileResponse,
    Incident,
    IncidentsByFieldResponse,
    IncidentsByWellResponseGrouped,
    IncidentTypePopup,
    ListPopupSummary,
    MaintenanceEquipmentInfoData,
    NptAct,
    PnvByWellResponse,
    StickingType,
    StickingTypeResponse,
} from '@features/incident/types';
import {AxiosProgressEvent} from 'axios';
import {PaginatedResponse} from 'types/pagination';
import {IIncidentsListItem} from '@features/operations/types';
import {FilesService} from '@services/Files/FilesService';
import {downloadAndExtractFileExtension} from 'utils/api/file';

const fileService = FilesService.getInstance();

function deleteData(params: ParamsType): Promise<unknown> {
    return api.delete('/npv', undefined, undefined, params);
}

function getData(params: ParamsType) {
    return api.get<PaginatedResponse<Incident[]>>(`/npv`, params);
}

function getIncident(id: string | number) {
    return api.get<Incident>(`/npv/${id}`);
}

function createData(data: Record<string, any>, params: ParamsType): Promise<Incident> {
    return api.post('/npv', data, {}, params);
}

function getPageSchema(wellId: string): Promise<PageSchema> {
    return api.get(`/npv/table/schema?wellId=${wellId}`);
}

function editData(data: Record<string, any>, params: ParamsType): Promise<Incident> {
    return api.put('/npv', data, undefined, params);
}

function getIncidentsByWell(params: {
    wellId: number | string;
    casingColumn?: string[];
    wellConstructionStage?: string[];
    type?: IncidentTypePopup;
}): Promise<IncidentsByWellResponseGrouped> {
    return api.get(`/dashboard/field-common-info/incidentsByWell`, params, undefined, params);
}

function getPnvByWell(params: {
    wellId: number | string;
    casingColumn?: string[];
    wellConstructionStage?: string[];
}): Promise<PnvByWellResponse> {
    return api.get(`/dashboard/field-common-info/pnvByWell`, params, undefined, params);
}

function getNpvByRunId(params: {
    runId: number | string;
    casingColumn?: string[];
    incidentType?: IncidentTypePopup;
    wellConstructionStage?: string[];
}): Promise<ListPopupSummary[]> {
    return api.get(`/bha-runs/${params.runId}/incident-summary-by-type`, params);
}

function getAdditionalWorkByWell(params: {
    wellId: number | string;
    casingColumn?: string[];
    wellConstructionStage?: string[];
}): Promise<AdditionalWorkByWellResponse> {
    return api.get(`/dashboard/field-common-info/additionalWorksByWell`, params, undefined, params);
}

function getIncidentsByWells(params: {
    wellId: string[];
    wellConstructionStage?: string[];
    type?: IncidentTypePopup;
}): Promise<IncidentsByWellResponseGrouped> {
    return api.get('/dashboard/field-common-info/incidentsByWells', params, undefined, params);
}

function getIncidentsByField(params: {
    field: string;
    wellId?: string[];
    wellConstructionStage?: string[];
    type?: IncidentTypePopup;
}): Promise<IncidentsByFieldResponse> {
    return api.get(`/dashboard/field-common-info/incidentsByField`, params, undefined, params);
}

function getIncidentTitleList(params: ParamsType) {
    return api.get<IIncidentsListItem[]>(`/npv/titles`, params);
}

function createFile(
    data: FormData,
    params: ParamsType,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    signal?: AbortSignal,
): Promise<CreateFileResponse> {
    // Явно используем instance, потому что api.post переопределяет ContentType и файл уходит на сервер некорректно
    return instance({
        method: 'POST',
        url: `/files/upload`,
        data,
        params,
        onUploadProgress,
        signal,
    });
}

function downloadKillSheetTemplate() {
    return fileService.downloadFileFromTable('/npv/import/killSheet-template').then((file) => {
        downloadAndExtractFileExtension(file);
    });
}

function getStickingTypeQuestions() {
    return api.get<StickingTypeResponse>(`/sticking-bk/questions`);
}

function getStickingTypes(data: StickingTypeResponse) {
    return api.post<StickingType[]>(`/sticking-bk/get-by-answers`, data);
}

function downloadStickingTypeAnswers(data: StickingTypeResponse) {
    return api.post<string[]>(`/export/sticking-bk-question-summary`, data);
}

function getMaintenanceEquipmentInfo(params: ParamsType) {
    return api.get<MaintenanceEquipmentInfoData[]>(`/maintenance/equipment/info`, params);
}

function getMaintenanceEquipmentReason(params: ParamsType) {
    return api.get<DictionaryItem[]>(`/maintenance/equipment/reason`, params);
}

function getNptActByIncidentId(incidentId: string | number) {
    return api.get<NptAct>(`/npv-act/by-incident?incidentId=${incidentId}`);
}
function createNptAct(data: Record<string, any>, params: ParamsType): Promise<NptAct> {
    return api.post('/npv-act', data, {}, params);
}

function editNptAct(data: Record<string, any>, params: ParamsType): Promise<NptAct> {
    return api.put('/npv-act', data, {}, params);
}

function deleteNptAct(id: string | number) {
    return api.delete('/npv-act', undefined, undefined, {id});
}

function getNptActById(id: string | number) {
    return api.get<NptAct>(`/npv-act/${id}`);
}
export const IncidentService = {
    deleteData,
    getData,
    createData,
    createFile,
    editData,
    getIncident,
    getPageSchema,
    getIcindentsByWell: getIncidentsByWell,
    getIncidentsByWells,
    getPnvByWell,
    getAdditionalWorkByWell,
    getIncidentsByField,
    getIncidentsList: getIncidentTitleList,
    downloadKillSheetTemplate,
    getStickingTypeQuestions,
    getStickingTypes,
    downloadStickingTypeAnswers,
    getNpvByRunId,
    getMaintenanceEquipmentInfo,
    getMaintenanceEquipmentReason,
    createNptAct,
    editNptAct,
    getNptActByIncidentId,
    deleteNptAct,
    getNptActById,
};
